import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import UploadProgress from "./UploadProgress";

import { handlePhotoUpload } from "../../filestackRequestViaApi";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setSessionStorage } from "../../../../sessionAndLocalStorage";
import {
  selectAppraisal,
  hasRequiredImagesForCategory,
  getNextPhotoGroup,
  selectImageUploadData,
  getHasAllRequiredPhotosUploaded
} from "../../../../store/selectors";
import { addImages, appraisalSlice } from "../../../../store/slices/Appraisal";
import { imageUploadSlice } from "../../../../store/slices/ImageUploads";
import { errorMessages } from "../../../../data/filterTypes";
import { useDispatch as useAxiosDispatch } from "../../context/axiosContext";

export default function UploadProgressContainer() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const {
    currentUploadHelper,
    currentImageIdentifiers,
    returnToGallery,
    images,
    currentUploads,
    requiredPhotoData
  } = useSelector(selectImageUploadData);
  const nextPhotoGroup = useSelector(getNextPhotoGroup);
  const axiosDispatch = useAxiosDispatch();

  const hasAllImagesForCategory = useSelector(
    hasRequiredImagesForCategory(currentUploadHelper)
  );

  const currentJourneyHasEnoughImages =
    images.length >=
    requiredPhotoData.reduce((runningTotal, element) => {
      runningTotal += element.imageQuantityRequired;
      return runningTotal;
    }, 0);

  const hasAllRequiredImagesUploaded =
    useSelector(getHasAllRequiredPhotosUploaded) &&
    currentJourneyHasEnoughImages;

  const [loading, setLoading] = useState(false);
  const {
    data: { appraisalSessionId },
    fileStack
  } = useSelector(selectAppraisal);
  const navigate = useNavigate();

  useEffect(() => {
    const currentHelper = requiredPhotoData.find((data) => data.id === id);
    if (currentHelper) {
      dispatch(imageUploadSlice.actions.setCurrentUploadHelper(currentHelper));
    }
  }, [id, dispatch, requiredPhotoData]);

  function handleSubmit() {
    const imagesWithoutBlob = images.map(({ imageSrc, ...rest }) => rest);

    setSessionStorage("images", imagesWithoutBlob);

    const imagePayload = currentImageIdentifiers.map((imageIdentifier) =>
      currentUploadHelper.tag === "Vehicle introduction"
        ? {
            imageIdentifier,
            imageTags: [
              {
                tagDescription: currentUploadHelper.tag
              },
              {
                tagDescription: currentUploadHelper.id
              }
            ]
          }
        : {
            imageIdentifier,
            imageTags: [
              {
                tagDescription: currentUploadHelper.tag
              }
            ]
          }
    );

    // images have been uploaded to filestack but not yet to appraisal
    if (currentImageIdentifiers.length > 0) {
      setLoading(true);
      const imageRequest = () =>
        dispatch(addImages({ images: imagePayload, appraisalSessionId })).then(
          (response) => {
            if (response.error?.message === errorMessages.NETWORKERROR) {
              dispatch(appraisalSlice.actions.setIsOnline(false));
              setTimeout(() => {
                imageRequest();
              }, 3000);
            } else {
              dispatch(appraisalSlice.actions.setIsOnline(true));
              dispatch(imageUploadSlice.actions.clearCurrentImageIdentifiers());
              if (hasAllRequiredImagesUploaded) {
                return navigate("/photo-gallery");
              }
              if (
                currentUploads.length >=
                  currentUploadHelper.imageQuantityRequired ||
                returnToGallery
              ) {
                // current group of uploads are more than or equal to min required for section)
                if (!nextPhotoGroup && !returnToGallery) {
                  // on last image group and havn't been navigated back here from gallery retake
                  return navigate("/photo-gallery");
                }

                if (returnToGallery) {
                  // have been navigated back here from gallery
                  return navigate("/photo-gallery");
                }

                if (hasAllImagesForCategory) {
                  // navigate to next section and clear validation message
                  return navigate(`/image-upload/${nextPhotoGroup.id}`);
                }
              }
            }
          }
        );
      imageRequest();
    }

    // if total images already uploaded matches photos group required photos
    if (hasAllImagesForCategory && !returnToGallery) {
      nextPhotoGroup
        ? navigate(`/image-upload/${nextPhotoGroup.id}`)
        : navigate("/photo-gallery");
    } else {
      setLoading(false);
    }
  }

  const handleChange = async (images) => {
    if (images) {
      handlePhotoUpload(
        images,
        dispatch,
        currentUploadHelper,
        axiosDispatch,
        appraisalSessionId
      );
    }
    dispatch(imageUploadSlice.actions.setRetryingRequest(0));
  };

  return (
    <>
      {currentUploadHelper && fileStack.keys && (
        <UploadProgress
          handleSubmit={handleSubmit}
          handleChange={handleChange}
          minPhotos={currentUploadHelper.imageQuantityRequired}
          currentUploadGroup={id}
          loading={loading}
          lastImage={!nextPhotoGroup}
          hasAllImages={hasAllImagesForCategory}
        />
      )}
    </>
  );
}

UploadProgressContainer.propTypes = {
  id: PropTypes.string
};
